import * as Crypto from "expo-crypto";

import { MAX_PROFILES } from "$src/core/constants/profile";
import type { HexColorDto, ProfileDto } from "$src/core/models/dto";
import Storage from "$src/core/services/storage/storage";

/**
 * Every profile lives only on this device: there is no profile backend, and
 * nothing here is ever sent to or read from the server. The profile derived
 * from the signed-in customer is copied in here once (see the API layer) as
 * the main profile; extra profiles can be added and deleted, the main one
 * cannot be removed.
 */
export const getLocalProfiles = (): ProfileDto[] => {
  const profiles = Storage.get("profiles") ?? [];

  if (profiles.length === 0 || profiles.some((profile) => profile.isMain)) {
    return profiles;
  }

  // Stores written before isMain: the first (seeded) profile is the main one.
  return profiles.map((profile, index) =>
    index === 0 ? { ...profile, isMain: true } : profile,
  );
};

/**
 * False until the store is first written. The API layer seeds the account
 * profile on that first read only.
 */
export const hasLocalProfileStore = (): boolean =>
  Storage.get("profiles") !== null;

/** Creates a profile on this device only, so it works while the API is down. */
export const addLocalProfile = async (
  name: string,
  tint: HexColorDto,
  options: { id?: string; isMain?: boolean } = {},
): Promise<ProfileDto> => {
  const profiles = getLocalProfiles();

  if (profiles.length >= MAX_PROFILES) {
    throw new Error("Maximum number of profiles reached");
  }

  const isMain = options.isMain ?? profiles.length === 0;

  const profile: ProfileDto = {
    id: options.id ?? Crypto.randomUUID(),
    name: name.trim(),
    tint,
    ...(isMain ? { isMain: true } : {}),
  };

  Storage.set("profiles", [...profiles, profile]);

  return profile;
};

/** Updates a profile on this device. Throws when the id is not a local one. */
export const updateLocalProfile = async (
  id: string,
  { name, tint }: { name: string; tint: HexColorDto },
): Promise<ProfileDto> => {
  const profiles = getLocalProfiles();
  const profile = profiles.find((candidate) => candidate.id === id);

  if (!profile) {
    throw new Error("Profile not found");
  }

  const updated: ProfileDto = { ...profile, name: name.trim(), tint };

  Storage.set(
    "profiles",
    profiles.map((candidate) => (candidate.id === id ? updated : candidate)),
  );

  return updated;
};

/** Deletes a profile from this device. Throws when the id is not a local one. */
export const deleteLocalProfile = async (id: string): Promise<void> => {
  const profiles = getLocalProfiles();
  const profile = profiles.find((candidate) => candidate.id === id);

  if (!profile) {
    throw new Error("Profile not found");
  }

  if (profile.isMain) {
    throw new Error("Cannot delete the main profile");
  }

  Storage.set(
    "profiles",
    profiles.filter((candidate) => candidate.id !== id),
  );
};
